/**
 * Diagramas para revisão — a fila de quem confere.
 *
 * **É a única tela do sistema que não é de um projeto.** Ela atravessa todos:
 * o `GET api/diagramas/para-revisao` não recebe projeto nenhum, e é assim que
 * tem de ser — quem revisa não abre projeto por projeto procurando o que
 * chegou; olha uma fila.
 *
 * Por isso ela mora no menu do topo, ao lado de Projetos, e agrupa por projeto:
 * revisar é trabalho de contexto, e vinte diagramas do mesmo projeto se revisam
 * juntos.
 *
 * **Os grupos abrem fechados**, e isso não é enfeite: hoje a fila tem 1.444
 * diagramas em 70 projetos. Desenhar as 1.444 linhas de uma vez trava a rolagem
 * e não ajuda — quem revisa escolhe um projeto e trabalha nele. Fechado, a tela
 * mostra 70 linhas e a contagem de cada uma.
 *
 * O que dá para fazer daqui é tirar da fila — voltar o diagrama para o estado
 * anterior à revisão. Publicar continua na tela do projeto, de propósito: são
 * decisões diferentes, e juntá-las convida a publicar sem olhar.
 */

import { useMemo, useState } from "react"
import { Link } from "react-router-dom"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { api, noProjeto, obter } from "@/api/cliente"
import { ErroDaApi, ErroDeTrava } from "@/api/erros"
import type { DiagramaResponse } from "@/api/diagramas"
import { BarraDeEstado } from "@/kit/BarraDeEstado"

const dia = (d: string | null) => {
  if (!d || d.slice(0, 4) === "1900" || d.slice(0, 4) === "0001") return ""
  const [a, m, x] = d.slice(0, 10).split("-")
  return `${x}/${m}/${a}`
}

export function FilaDeRevisao() {
  const qc = useQueryClient()

  const fila = useQuery({
    queryKey: ["diagramas-revisao"] as const,
    queryFn: () => obter<DiagramaResponse[]>("api/diagramas/para-revisao", {}),
  })

  const tirarDaFila = useMutation({
    mutationFn: async (d: DiagramaResponse) => {
      await api.post(`api/diagramas/${d.id}/remover-revisao`, undefined, noProjeto(d.projetoId))
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["diagramas-revisao"] }),
  })

  /** Agrupa por projeto: revisar é trabalho de contexto. */
  const porProjeto = useMemo(() => {
    const mapa = new Map<number, DiagramaResponse[]>()
    for (const d of fila.data ?? []) {
      const lista = mapa.get(d.projetoId) ?? []
      lista.push(d)
      mapa.set(d.projetoId, lista)
    }
    return [...mapa.entries()].sort((a, b) => b[1].length - a[1].length)
  }, [fila.data])

  const total = (fila.data ?? []).length
  const [abertos, setAbertos] = useState<Set<number>>(new Set())

  function alternar(projetoId: number) {
    setAbertos((s) => {
      const n = new Set(s)
      n.has(projetoId) ? n.delete(projetoId) : n.add(projetoId)
      return n
    })
  }

  return (
    <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      <div
        className="barra"
        style={{
          height: 48, flex: "0 0 48px", display: "flex", alignItems: "center", gap: 12,
          padding: "0 18px", background: "var(--surface)", borderBottom: "1px solid var(--line)",
        }}
      >
        <span style={{ fontSize: 15, fontWeight: 600 }}>Diagramas para revisão</span>
        <span style={{ fontSize: 12, color: "var(--txt2)" }}>
          A fila de todos os projetos, agrupada por projeto.
        </span>
      </div>

      <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: 18 }}>
        {fila.isPending ? (
          <p style={{ fontSize: 12.5, color: "var(--txt2)" }}>Carregando a fila…</p>
        ) : fila.error ? (
          <p style={{ fontSize: 12.5, color: "var(--erro)" }}>
            {fila.error instanceof ErroDaApi ? fila.error.message : "Não foi possível ler a fila."}
          </p>
        ) : total === 0 ? (
          <p style={{ fontSize: 13, color: "var(--txt2)", maxWidth: "48ch", lineHeight: 1.6 }}>
            <b>Nada na fila.</b><br />
            <span style={{ fontSize: 12, color: "var(--txt3)" }}>
              Diagramas entram aqui quando alguém os manda para revisão, na tela de Diagramas do
              projeto.
            </span>
          </p>
        ) : (
          <div style={{ maxWidth: 860, display: "flex", flexDirection: "column", gap: 16 }}>
            {porProjeto.map(([projetoId, diagramas]) => (
              <section key={projetoId} style={{
                border: "1px solid var(--line2)", borderRadius: 10, overflow: "hidden",
                background: "var(--surface)",
              }}>
                <div style={{
                  display: "flex", alignItems: "center", gap: 10, padding: "9px 13px",
                  background: "var(--surface3)",
                  borderBottom: abertos.has(projetoId) ? "1px solid var(--line)" : undefined,
                }}>
                  <button
                    onClick={() => alternar(projetoId)}
                    aria-expanded={abertos.has(projetoId)}
                    style={{
                      display: "inline-flex", alignItems: "center", gap: 7,
                      fontSize: 12.5, fontWeight: 600, color: "var(--txt)",
                    }}
                  >
                    <span aria-hidden="true" style={{ fontSize: 10, color: "var(--txt3)" }}>
                      {abertos.has(projetoId) ? "▾" : "▸"}
                    </span>
                    projeto <span className="num">{projetoId}</span>
                  </button>
                  <span style={{ fontSize: 11.5, color: "var(--txt3)" }}>
                    {diagramas.length} {diagramas.length === 1 ? "diagrama" : "diagramas"}
                  </span>
                  <span style={{ marginLeft: "auto" }} />
                  <Link to={`/projetos/${projetoId}/diagramas`}
                        style={{ fontSize: 11.5, fontWeight: 600 }}>
                    abrir o projeto
                  </Link>
                </div>

                {abertos.has(projetoId) && diagramas.map((d, i) => (
                  <div key={d.id} style={{
                    display: "flex", alignItems: "center", gap: 10, padding: "9px 13px",
                    borderTop: i === 0 ? undefined : "1px solid var(--line)",
                  }}>
                    <span className="num" style={{ flex: "0 0 62px", fontSize: 12, color: "var(--txt2)" }}>
                      {d.id}
                    </span>
                    <span style={{ flex: 1, minWidth: 0, fontSize: 12.5 }}>
                      <span className="mono" style={{ fontWeight: 600 }}>
                        {d.componenteCodigo || "—"}
                      </span>
                      {d.componenteDescricao
                        && d.componenteDescricao.toLowerCase() !== (d.componenteCodigo || "").toLowerCase()
                        && <> {d.componenteDescricao}</>}
                      {d.obs && (
                        <span style={{ display: "block", fontSize: 11, color: "var(--txt3)" }}>{d.obs}</span>
                      )}
                    </span>
                    {dia(d.revisaoData) && (
                      <span className="num" style={{ fontSize: 11.5, color: "var(--txt3)" }}>
                        desde {dia(d.revisaoData)}
                      </span>
                    )}
                    {d.publicado && (
                      <span style={{
                        display: "inline-flex", alignItems: "center", height: 19, padding: "0 8px",
                        borderRadius: 999, fontSize: 10.5, fontWeight: 600,
                        background: "var(--ok-soft)", color: "var(--ok-on-soft)",
                      }}>
                        no ar
                      </span>
                    )}
                    <button
                      onClick={() => tirarDaFila.mutate(d)}
                      disabled={tirarDaFila.isPending}
                      title="Tira este diagrama da fila de revisão"
                      style={{
                        height: 26, padding: "0 10px", borderRadius: 6, fontSize: 11.5, fontWeight: 600,
                        border: "1px solid var(--line2)", background: "var(--surface)", color: "var(--txt2)",
                      }}
                    >
                      tirar da fila
                    </button>
                  </div>
                ))}
              </section>
            ))}
          </div>
        )}

        {tirarDaFila.error != null && (
          <div style={{
            marginTop: 14, maxWidth: 860, padding: "10px 12px", borderRadius: 8,
            fontSize: 11.5, lineHeight: 1.5,
            background: tirarDaFila.error instanceof ErroDeTrava ? "var(--acao-soft)" : "var(--erro-soft)",
            color: tirarDaFila.error instanceof ErroDeTrava ? "var(--acao-on-soft)" : "var(--erro-on-soft)",
          }}>
            {tirarDaFila.error instanceof Error ? tirarDaFila.error.message : "Não foi possível."}
          </div>
        )}
      </div>

      <BarraDeEstado>
        {total > 0 && (
          <span>
            <b className="num" style={{ color: "var(--txt)" }}>{total}</b>{" "}
            {total === 1 ? "diagrama esperando" : "diagramas esperando"}
            {" em "}
            <b className="num" style={{ color: "var(--txt)" }}>{porProjeto.length}</b>{" "}
            {porProjeto.length === 1 ? "projeto" : "projetos"}
          </span>
        )}
        <span style={{ color: "var(--txt3)" }}>
          Publicar continua na tela do projeto — são decisões diferentes.
        </span>
      </BarraDeEstado>
    </div>
  )
}
